import React, { useState, useEffect, SyntheticEvent, useRef, FormEvent } from 'react';
import styled from 'styled-components';
import { Loader, Dimmer, Checkbox } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from '../../components/common/toast';
import { RootState } from 'StoreModel';
import {
  Separator,
  Column,
  PageHeading,
  NoBackgroundButton,
  LoginButton,
} from '../../components/styled-components/common';
import { fetchCommunitySignupDetails } from '../../services/api/community';
import { signupApi } from '../../services/api/session';
import { Community } from 'redux/types/account';
import useWindowDimensions from '../../util/windowSize';
import { googleSignIn, loginAsync } from 'redux/actions/session';
import { StyledButton } from 'primitives/Button/style';
import { getLanguageOptions } from 'util/utils';
import { getLiteralsAsync } from 'redux/actions/account';
import { Languages } from 'redux/types/enums';
import SelectDropdown from 'components/common/MuiDropdown';
import LazyImage from 'components/common/lazyImage';
import SignupComponent from 'components/account/SignupComponent';
import {
  Container,
  FormContainer,
  SignUpFormContainer,
  ModalSignupFormContainer,
  Label,
  Input,
  Row,
  FooterSpan,
  TCLabel,
  TCLink,
  MainBox,
  TextBox,
  Content,
  SignupButtonContainer,
  SignupComponentContainer,
  EmailSignupButton,
  SignupSectionContainer,
  InputHolder,
} from './signUpStyles';
const SITEKEY = process.env.REACT_APP_RECAPTCHA_SITEKEY || '';

const mapDispatchToProps = {
  googleSignIn: googleSignIn.request,
  getLIterals: getLiteralsAsync.request,
  login: loginAsync.request,
};

const mapStateToProps = (state: RootState) => ({
  bearer: state.account.session.session.bearer,
  literals: state.literals,
  applicationDefinitions: state.account.applicationDefinitions,
});

type dispatchType = typeof mapDispatchToProps;
interface Props extends ReturnType<typeof mapStateToProps>, dispatchType {
  insideModal?: boolean;
  setAccountConfirmation?: React.Dispatch<React.SetStateAction<boolean>>;
}

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const SignUp: React.FC<Props> = props => {
  const { literals, applicationDefinitions, getLIterals, insideModal = false, setAccountConfirmation, login } = props;
  const queryParams = useQuery();
  const [email, setEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [password, setPassword] = useState<string>();
  const [confirmation, setConfirmation] = useState<string>('');
  const [community, setCommunity] = useState<Community | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [accountCreated, setAccountCreated] = useState<boolean>(false);
  const [accountActive, setAccountActive] = useState<boolean>(false);
  const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [recaptchaResponse, setRecaptchaResponse] = useState<string>('');
  const [placeFormOnTop, setPlaceFormOnTop] = useState<boolean>(false);
  const [redirectAfterFirstLogin, setRedirectAfterFirstLogin] = useState<string | null>(null);
  const [userLanguage, _setUserLanguage] = useState<Languages>(Languages.EN);
  const [signUpViaEmail, setSignUpViaEmail] = useState<boolean>(false);
  const [existingAccount, setExistingAccount] = useState<boolean>(false);
  const containerRef = useRef(null);
  const location = useLocation<{ appId: string }>();
  const history = useHistory();

  const windowSize = useWindowDimensions();

  useEffect(() => {
    if (containerRef && containerRef.current) {
      if ((containerRef as any).current.scrollHeight >= windowSize.height) {
        setPlaceFormOnTop(true);
      } else {
        setPlaceFormOnTop(false);
      }
    }
  });

  useEffect(() => {
    if (applicationDefinitions?.length) {
      setRedirectAfterFirstLogin(`/application-definition/${applicationDefinitions[0].id}/new-application`);
      applicationDefinitions[0]?.language && _setUserLanguage(applicationDefinitions[0].language);
    }
  }, [applicationDefinitions]);

  const targetCommunityId = parseInt(queryParams.get('to') || '0');
  let targetCircleId: number | null = queryParams.get('circleId') ? parseInt(queryParams.get('circleId')!) : null;
  if (targetCircleId !== null && isNaN(targetCircleId)) {
    targetCircleId = null;
  }

  let targetTags: string[] | null = null;
  const tagsFromQuery = queryParams.get('tags');

  if (tagsFromQuery) {
    try {
      const decodedTags = decodeURIComponent(tagsFromQuery);

      const cleanedTags = decodedTags.replace(/'%27/g, "'").replace(/^\[|\]$/g, '');
      const tagArray = cleanedTags.split(',').map(tag => tag.trim().replace(/^'(.*)'$/, '$1'));

      if (Array.isArray(tagArray) && tagArray.every(tag => typeof tag === 'string')) {
        targetTags = tagArray;
      }
    } catch (error) {
      targetTags = null;
    }
  }
  const _onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (existingAccount) {
      if (!password) return;
      login({
        username: email,
        password: password,
        recaptchaResponse: recaptchaResponse,
        communityId: targetCommunityId,
        circleId: targetCircleId,
        tags: targetTags,
      });
    } else {
      if (confirmation !== password) {
        toast(literals.public_signup_password_match, { type: 'error' });
        return;
      }

      if (!acceptTerms) {
        toast(literals.sign_up_terms_must_be_accepted, { type: 'error' });
        return;
      }

      if (recaptchaResponse === '') {
        toast(literals.auth_check_recaptcha, { type: 'error' });
        return;
      }

      setIsCreatingAccount(true);
      signupApi({
        firstName,
        lastName,
        mail: email,
        password,
        primaryCommunityId: targetCommunityId,
        circleId: targetCircleId,
        tags: targetTags,
        recaptchaResponse,
        appId: location?.state?.appId || null,
        redirectAfterFirstLogin,
        language: userLanguage,
      })
        .then(response => {
          if (response?.isActive) setAccountActive(true);
          else setAccountCreated(true);
          if (setAccountConfirmation) setAccountConfirmation(true);
        })
        .catch(err => {
          setPassword('');
          setConfirmation('');
          setAcceptTerms(false);
          setRecaptchaResponse('');
          // TODO: Can be removed after literal error message is added to the server
          if (err.response.data === 'Babele.Domain.Aggregates.UserAgg.Exceptions.UserNotInvitedException')
            toast(literals.auth_register_no_invitation, { type: 'warning' });
          else {
            const error =
              err?.response?.data &&
              typeof err?.response?.data === 'string' &&
              literals[err.response.data.replaceAll('.', '')];
            toast(error || literals.public_singup_error_message, { type: 'warning' });
          }
        })
        .finally(() => setIsCreatingAccount(false));
    }
  };

  const recaptchaOnChange = (value: any) => {
    setRecaptchaResponse(value);
  };

  useEffect(() => {
    fetchCommunitySignupDetails(targetCommunityId === 0 ? 163 : targetCommunityId)
      .then(c => setCommunity(c))
      .catch(err => {
        const error = err?.response?.data && literals[err.response.data.replaceAll('.', '')];
        toast(error || literals.public_singup_fetch_community_error, { type: 'error' });
      })
      .finally(() => setIsFetching(false));
  }, []);

  useEffect(() => {
    if (accountActive) history.push('/login', { accountActive: true });
  }, [accountActive]);

  if (isNaN(targetCommunityId)) return <span>{literals.public_signup_wrong_community_id_error}</span>;

  useEffect(() => {
    getLIterals({ language: userLanguage });
  }, [userLanguage]);

  const setUserLanguage = (event: any) => {
    if (event?.target?.value) _setUserLanguage(event.target.value);
  };
  const LoginForm = () => {
    return (
      <form autoComplete="off" style={{ display: 'flex', flexDirection: 'column' }} onSubmit={_onSubmit}>
        {/* to let the browser use these 2 fields for auto filling  */}
        <input type="email" name="hidden" id="hidden" style={{ width: 0, height: 0, border: 0, padding: 0 }} />
        <input type="password" name="hidden" id="hidden" style={{ width: 0, height: 0, border: 0, padding: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1em', justifyContent: 'center' }}>
          <Row>
            <InputHolder>
              <Label>{literals.public_signup_email_placeholder}</Label>
              <Input
                value={email}
                required
                placeholder={literals.public_signup_email_placeholder}
                type="email"
                onChange={event => setEmail(event.target.value)}
                autoComplete="off"
              />
            </InputHolder>
          </Row>

          <Row>
            <InputHolder>
              <Label>{literals.public_signup_password_placeholder}</Label>
              <Input
                required
                value={password}
                placeholder={literals.public_signup_password_placeholder}
                type="password"
                onInvalid={(event: FormEvent<HTMLInputElement>) =>
                  (event.target as HTMLInputElement).setCustomValidity(
                    (literals.validation_please_use_X_characters || '').replace('{0}', '8'),
                  )
                }
                onInput={(event: FormEvent<HTMLInputElement>) =>
                  (event.target as HTMLInputElement).setCustomValidity('')
                }
                onChange={event => setPassword(event.target.value)}
                autoComplete="off"
              />
            </InputHolder>
          </Row>
          <Row>
            <ReCAPTCHA sitekey={SITEKEY} onChange={recaptchaOnChange} />
          </Row>

          <Row style={{ marginBottom: '1em' }}>
            <EmailSignupButton type="submit">{literals.log_on_log_in_title}</EmailSignupButton>
          </Row>

          <FooterSpan>
            {literals.log_on_forgot_password}
            {` `}
            <Link to="/forgot-password">{literals.reset_password}</Link>
          </FooterSpan>
        </div>
      </form>
    );
  };
  const SignupForm = () => {
    return (
      <form autoComplete="off" style={{ display: 'flex', flexDirection: 'column' }} onSubmit={_onSubmit}>
        {/* to let the browser use these 2 fields for auto filling  */}
        <input type="email" name="hidden" id="hidden" style={{ width: 0, height: 0, border: 0, padding: 0 }} />
        <input type="password" name="hidden" id="hidden" style={{ width: 0, height: 0, border: 0, padding: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
          <Row>
            <InputHolder>
              <Label>{literals.public_signup_email_placeholder}</Label>
              <Input
                value={email}
                required
                placeholder={literals.public_signup_email_placeholder}
                type="email"
                onChange={event => setEmail(event.target.value)}
                autoComplete="off"
              />
            </InputHolder>
          </Row>
          <Row>
            <InputHolder>
              <Label>{literals.public_signup_firstname}</Label>
              <Input
                value={firstName}
                required
                placeholder={literals.public_signup_firstname}
                type="text"
                onChange={event => setFirstName(event.target.value)}
                autoComplete="off"
              />
            </InputHolder>
            <InputHolder>
              <Label>{literals.public_signup_last_name_placeholder}</Label>
              <Input
                value={lastName}
                required
                placeholder={literals.public_signup_last_name_placeholder}
                type="text"
                onChange={event => setLastName(event.target.value)}
                autoComplete="off"
              />
            </InputHolder>
          </Row>
          <Row>
            <InputHolder>
              <Label>{literals.public_signup_password_placeholder}</Label>
              <Input
                required
                value={password}
                pattern=".{8,}"
                placeholder={literals.public_signup_password_placeholder}
                type="password"
                onInvalid={(event: FormEvent<HTMLInputElement>) =>
                  (event.target as HTMLInputElement).setCustomValidity(
                    (literals.validation_please_use_X_characters || '').replace('{0}', '8'),
                  )
                }
                onInput={(event: FormEvent<HTMLInputElement>) =>
                  (event.target as HTMLInputElement).setCustomValidity('')
                }
                onChange={event => setPassword(event.target.value)}
                autoComplete="off"
              />
            </InputHolder>
            <InputHolder>
              <Label>{literals.public_signup_confirm_password_placeholder}</Label>
              <Input
                required
                value={confirmation}
                placeholder={literals.public_signup_confirm_password_placeholder}
                pattern=".{8,}"
                type="password"
                onInvalid={(event: FormEvent<HTMLInputElement>) =>
                  (event.target as HTMLInputElement).setCustomValidity(
                    (literals.validation_please_use_X_characters || '').replace('{0}', '8'),
                  )
                }
                onInput={(event: FormEvent<HTMLInputElement>) =>
                  (event.target as HTMLInputElement).setCustomValidity('')
                }
                onChange={event => setConfirmation(event.target.value)}
                autoComplete="off"
              />
            </InputHolder>
          </Row>
          <Row>
            <ReCAPTCHA sitekey={SITEKEY} onChange={recaptchaOnChange} />
          </Row>
          <Row style={{ alignItems: 'center', gap: '0.5em' }}>
            <Checkbox
              checked={acceptTerms}
              onChange={(event: SyntheticEvent, data: any) => setAcceptTerms(data.checked)}
            />
            <TCLabel>
              {literals.public_signup_i_accept_the + ' '}
              <TCLink
                href="https://babele.co/home/terms-of-use/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="External link to Clutch.com"
              >
                {literals.public_signup_terms_amp_conditions}
              </TCLink>
            </TCLabel>
          </Row>
          <Row style={{ marginBottom: '1em' }}>
            <EmailSignupButton type="submit">{literals.sign_up_label}</EmailSignupButton>
          </Row>
        </div>
      </form>
    );
  };

  if (isFetching || isCreatingAccount)
    return (
      <Dimmer active={true} inverted={true}>
        <Loader inverted={true}>{literals.project_list_loading_tag_message}</Loader>
      </Dimmer>
    );

  if (!community) {
    return (
      <React.Fragment>
        <Container placeFormOnTop={false}>
          <FormContainer style={{ width: 'unset' }}>
            <h2 style={{ textAlign: 'center' }}>{literals.public_singup_missing_community_error}</h2>
          </FormContainer>
        </Container>
      </React.Fragment>
    );
  }

  if (accountCreated) {
    if (insideModal) {
      return (
        <ModalSignupFormContainer style={{ width: 'unset' }}>
          <h2 style={{ textAlign: 'center' }}>{literals.public_signup_success_message} 🚀</h2>
          <h3 style={{ fontWeight: 'normal', lineHeight: '1em', textAlign: 'center' }}>
            {literals.public_signup_thank_you_for_signing_up_msg}
          </h3>
          <h3 style={{ fontWeight: 'normal', lineHeight: '1em', textAlign: 'center' }}>
            {literals.page_title_log_into_account} <Link to="/login">{literals.log_on_log_in_title}</Link>
          </h3>
        </ModalSignupFormContainer>
      );
    }
    return (
      <React.Fragment>
        <Container placeFormOnTop={false}>
          <FormContainer style={{ width: 'unset' }}>
            <h2 style={{ textAlign: 'center' }}>{literals.public_signup_success_message} 🚀</h2>
            <h3 style={{ fontWeight: 'normal', lineHeight: '1em', textAlign: 'center' }}>
              {literals.public_signup_thank_you_for_signing_up_msg}
            </h3>
            <h3 style={{ fontWeight: 'normal', lineHeight: '1em', textAlign: 'center' }}>
              {literals.page_title_log_into_account} <Link to="/login">{literals.log_on_log_in_title}</Link>
            </h3>
          </FormContainer>
        </Container>
      </React.Fragment>
    );
  } else if (insideModal) {
    return <ModalSignupFormContainer ref={containerRef}>{SignupForm()}</ModalSignupFormContainer>;
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{literals.auth_register_helmet}</title>
      </Helmet>
      {targetCommunityId ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5em', alignItems: 'center' }}>
          <MainBox img={community.coverImage || ''}>
            <LazyImage src={community.logo || ''} width="100px" height="100px" />
            <TextBox>{community.name}</TextBox>
          </MainBox>
          <SignupSectionContainer signUpViaEmail={signUpViaEmail}>
            {signUpViaEmail ? (
              <>
                <NoBackgroundButton
                  style={{ display: 'flex', paddingTop: '0', flex: 1 }}
                  onClick={() => {
                    setSignUpViaEmail(false);
                    setExistingAccount(false);
                  }}
                >
                  <div>{`<`}</div>
                  <div>Back</div>
                </NoBackgroundButton>
                <SignupButtonContainer style={{ margin: '0 auto' }}>
                  <PageHeading style={{ textTransform: 'none' }}>{literals.signup_page_heading_message}</PageHeading>
                  <SignUpFormContainer ref={containerRef}>
                    {existingAccount ? LoginForm() : SignupForm()}
                    <Separator />
                    <FooterSpan>
                      {existingAccount
                        ? literals.sign_in_with_email_text
                        : literals.public_signup_already_have_an_account}{' '}
                      <NoBackgroundButton
                        style={{ padding: '0', marginLeft: '8px', lineHeight: 'normal' }}
                        onClick={() => {
                          setExistingAccount(!existingAccount);
                        }}
                      >
                        {existingAccount ? literals.sign_up_label : literals.log_on_log_in_title}
                      </NoBackgroundButton>
                    </FooterSpan>
                  </SignUpFormContainer>
                </SignupButtonContainer>
                <SelectDropdown
                  styles={{ flex: 1 }}
                  onChange={e => setUserLanguage(e)}
                  value={userLanguage}
                  menuItems={getLanguageOptions(literals)}
                />
              </>
            ) : (
              <SignupButtonContainer style={{ margin: '0 auto' }}>
                <PageHeading style={{ textTransform: 'none' }}>{literals.signup_page_heading_message}</PageHeading>
                <SignupComponentContainer>
                  <SignupComponent setSignUpViaEmail={() => setSignUpViaEmail(true)} />
                  <Separator />
                  <FooterSpan>
                    {literals.public_signup_already_have_an_account}{' '}
                    <NoBackgroundButton
                      style={{ padding: '0', marginLeft: '8px', lineHeight: 'normal' }}
                      onClick={() => {
                        setExistingAccount(true);
                        setSignUpViaEmail(true);
                      }}
                    >
                      {literals.log_on_log_in_title}
                    </NoBackgroundButton>
                  </FooterSpan>
                </SignupComponentContainer>
              </SignupButtonContainer>
            )}
          </SignupSectionContainer>
        </div>
      ) : (
        <Container placeFormOnTop={placeFormOnTop}>
          <FormContainer ref={containerRef}>
            <SelectDropdown
              styles={{ alignSelf: 'flex-end' }}
              onChange={e => setUserLanguage(e)}
              value={userLanguage}
              menuItems={getLanguageOptions(literals)}
            />
            <form onSubmit={_onSubmit}>
              {targetCommunityId ? (
                <h1 style={{ lineHeight: '1.1em' }}>
                  {literals.community_join_action_join} {community.name}
                </h1>
              ) : (
                <h1 style={{ lineHeight: '1.1em' }}>{literals.public_signup_title}</h1>
              )}

              <Separator />
              <Row>
                <InputHolder style={{ marginRight: '1em' }}>
                  <Label>{literals.public_signup_firstname}</Label>
                  <Input
                    value={firstName}
                    required
                    placeholder={literals.public_signup_firstname}
                    type="text"
                    onChange={event => setFirstName(event.target.value)}
                  />
                </InputHolder>
                <InputHolder>
                  <Label>{literals.public_signup_last_name_placeholder}</Label>
                  <Input
                    value={lastName}
                    required
                    placeholder={literals.public_signup_last_name_placeholder}
                    type="text"
                    onChange={event => setLastName(event.target.value)}
                  />
                </InputHolder>
              </Row>
              <InputHolder>
                <Label>{literals.public_signup_email_placeholder}</Label>
                <Input
                  value={email}
                  required
                  placeholder={literals.public_signup_email_placeholder}
                  type="email"
                  onChange={event => setEmail(event.target.value)}
                />
              </InputHolder>
              <Row>
                <InputHolder style={{ marginRight: '1em' }}>
                  <Label>{literals.public_signup_password_placeholder}</Label>
                  <Input
                    required
                    value={password}
                    pattern=".{8,}"
                    placeholder={literals.public_signup_password_placeholder}
                    type="password"
                    onInvalid={(event: FormEvent<HTMLInputElement>) =>
                      (event.target as HTMLInputElement).setCustomValidity(
                        (literals.validation_please_use_X_characters || '').replace('{0}', '8'),
                      )
                    }
                    onInput={(event: FormEvent<HTMLInputElement>) =>
                      (event.target as HTMLInputElement).setCustomValidity('')
                    }
                    onChange={event => setPassword(event.target.value)}
                  />
                </InputHolder>
                <InputHolder>
                  <Label>{literals.public_signup_confirm_password_placeholder}</Label>
                  <Input
                    required
                    value={confirmation}
                    placeholder={literals.public_signup_confirm_password_placeholder}
                    pattern=".{8,}"
                    type="password"
                    onInvalid={(event: FormEvent<HTMLInputElement>) =>
                      (event.target as HTMLInputElement).setCustomValidity(
                        (literals.validation_please_use_X_characters || '').replace('{0}', '8'),
                      )
                    }
                    onInput={(event: FormEvent<HTMLInputElement>) =>
                      (event.target as HTMLInputElement).setCustomValidity('')
                    }
                    onChange={event => setConfirmation(event.target.value)}
                  />
                </InputHolder>
              </Row>
              <Row style={{ alignItems: 'center', margin: '1em 0' }}>
                <Checkbox
                  checked={acceptTerms}
                  onChange={(event: SyntheticEvent, data: any) => setAcceptTerms(data.checked)}
                />
                <TCLabel>
                  {literals.public_signup_i_accept_the + ' '}
                  <TCLink
                    href="https://babele.co/home/terms-of-use/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="External link to Clutch.com"
                  >
                    {literals.public_signup_terms_amp_conditions}
                  </TCLink>
                </TCLabel>
              </Row>
              <Row>
                <ReCAPTCHA sitekey={SITEKEY} onChange={recaptchaOnChange} />
              </Row>
              <Row style={{ flexDirection: 'row', justifyContent: 'center', marginTop: '1em' }}>
                <StyledButton type="Submit">{literals.public_singup_create_account_button}</StyledButton>
              </Row>
            </form>
            <br />
            <Separator />
            <FooterSpan>
              {literals.public_signup_already_have_an_account} <Link to="/login">{literals.log_on_log_in_title}</Link>
            </FooterSpan>
          </FormContainer>
        </Container>
      )}
    </React.Fragment>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
